import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Observable, Subject, Subscription, combineLatest, of, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, take, switchMap, catchError } from 'rxjs/operators';

import { Conversation, ListRequest, WhereClause } from 'src/models';

import { ConversationsService } from '../../../services/conversations.service';
import { CustomCommandBarService, CommandBarAction } from '../../../services/custom-command-bar.service';

@Component({
  selector: 'app-custom-command-bar',
  templateUrl: './custom-command-bar.component.html',
  styleUrls: ['./custom-command-bar.component.scss'],
})
export class CustomCommandBarComponent implements OnInit, OnDestroy {
  isOpen$: Observable<boolean>;
  filteredActions: (CommandBarAction & { type?: string; action?: () => void })[] = [];
  displayedActions: CommandBarAction[] = [];
  displayedConversations: (CommandBarAction & { type?: string; action?: () => void })[] = [];
  searchTerm: string = '';
  private searchTermChanged = new Subject<string>();
  private subscriptions = new Subscription();
  activeIndex: number = 0; // For keyboard navigation

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef<HTMLInputElement>;

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    this.customCommandBarService.close();
  }

  constructor(
    public customCommandBarService: CustomCommandBarService,
    private conversationsService: ConversationsService,
    private navController: NavController,
    private elementRef: ElementRef, // For DOM manipulations if needed
  ) {
    this.isOpen$ = this.customCommandBarService.isOpen$;
  }

  ngOnInit(): void {
    const debouncedSearchTerm$ = this.searchTermChanged.pipe(
      distinctUntilChanged(),
      map((term) => term.toLowerCase()),
      startWith(''),
    );

    const conversationSearch$: Observable<(CommandBarAction & { type?: string; action?: () => void })[]> =
      debouncedSearchTerm$.pipe(
        switchMap((term) => {
          if (!term || term.length < 2) {
            return of([]);
          }
          const listOptions: ListRequest = {
            search: term,
            limit: 5,
          };
          return from(this.conversationsService.list(listOptions)).pipe(
            map((response) => {
              return (response.data || []).map(
                (convo: Partial<Conversation>) =>
                  ({
                    id: convo.id!,
                    name: `[#${convo.friendlyId}] ${convo.title}`,
                    callback: () => {
                      this.navController.navigateRoot(`/dashboard/inbox/all/conversations/${convo.id}`);
                      this.customCommandBarService.close();
                    },
                    type: 'conversation',
                    icon: 'chatbox-ellipses-outline',
                  }) as CommandBarAction & { type?: string; action?: () => void },
              );
            }),
            catchError((error) => {
              console.error('Error fetching conversations:', error);
              return of([]);
            }),
          );
        }),
        startWith([]),
      );

    this.subscriptions.add(
      combineLatest([this.customCommandBarService.actions$, debouncedSearchTerm$, conversationSearch$]).subscribe(
        ([actions, term, conversationActions]) => {
          if (!term) {
            this.displayedActions = actions.map((a) => ({ ...a }));
          } else {
            this.displayedActions = actions
              .filter(
                (action) =>
                  action.name.toLowerCase().includes(term) ||
                  (action.keywords && action.keywords.toLowerCase().includes(term)),
              )
              .map((a) => ({ ...a }));
          }

          this.displayedConversations = conversationActions;

          this.filteredActions = [...this.displayedActions, ...this.displayedConversations];
          this.activeIndex = 0;
        },
      ),
    );

    // Focus the input when the command bar opens
    this.subscriptions.add(
      this.isOpen$.subscribe((isOpen) => {
        if (isOpen) {
          setTimeout(() => {
            this.searchInput?.nativeElement.focus();
            this.searchTermChanged.next('');
            this.searchTerm = '';
          }, 50);
        }
      }),
    );
  }

  onSearchTermChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTermChanged.next(value);
  }

  selectAction(item: CommandBarAction & { type?: string; action?: () => void }): void {
    if (item.type === 'conversation') {
      if (typeof item.callback === 'function') {
        item.callback();
      }
    } else {
      this.customCommandBarService.triggerAction(item.id);
    }
  }

  formatShortcut(shortcut: CommandBarAction['shortcut']): string {
    if (!shortcut) {
      return '';
    }
    let parts: string[] = [];
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    if (shortcut.metaKey) {
      parts.push(isMac ? '⌘' : 'Ctrl');
    } else if (shortcut.ctrlKey) {
      parts.push('Ctrl');
    }
    if (shortcut.altKey) {
      parts.push(isMac ? '⌥' : 'Alt');
    }
    if (shortcut.shiftKey) {
      parts.push(isMac ? '⇧' : 'Shift');
    }
    parts.push(shortcut.key.toUpperCase());
    return parts.join(isMac ? '' : '+');
  }

  @HostListener('keydown.arrowdown', ['$event'])
  onArrowDown(event: KeyboardEvent) {
    if (!this.isOpen) return;
    event.preventDefault();
    if (this.activeIndex < this.filteredActions.length - 1) {
      this.activeIndex++;
      this.ensureActiveVisible();
    }
  }

  @HostListener('keydown.arrowup', ['$event'])
  onArrowUp(event: KeyboardEvent) {
    if (!this.isOpen) return;
    event.preventDefault();
    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.ensureActiveVisible();
    }
  }

  @HostListener('keydown.enter', ['$event'])
  onEnter(event: KeyboardEvent) {
    if (!this.isOpen) return;
    event.preventDefault();
    if (this.filteredActions.length > 0 && this.activeIndex >= 0 && this.activeIndex < this.filteredActions.length) {
      this.selectAction(this.filteredActions[this.activeIndex]);
    }
  }

  private get isOpen(): boolean {
    let open = false;
    this.isOpen$.pipe(take(1)).subscribe((val) => (open = val));
    return open;
  }

  private ensureActiveVisible(): void {
    setTimeout(() => {
      const activeItem = this.elementRef.nativeElement.querySelector('.action-item.active');
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'nearest' });
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
